import { merge } from "lodash-es";

import { IDataKeys } from "shared/types";
import { ISuggestEdit, IVenue, IVenuePostData } from "Venues/types";
import { APIService } from "shared/services";
import { API_ENDPOINTS } from "shared/constants";
import { prefixWebsite } from "shared/utils";
import { IJSONAPIData, IJSONAPIItem } from "shared/types";
import { IVenuePatchData } from "Venues/types";
import { CancelToken } from "axios";

interface IJSONAPIVenue {
  [key: string]: IJSONAPIItem;
}

export interface IJSONAPIVenueCollection {
  [IDataKeys.venue]: IJSONAPIVenue;
  meta: {
    [key: string]: IJSONAPIItem;
  };
  pagination: any;
}

export class VenueService {
  private static getCleanVenue = (
    venue: IVenue | IVenuePostData | IVenuePatchData
  ) => {
    // Add "http://" to website if it is missing
    let website = venue.contact?.website;
    if (website) {
      website = prefixWebsite(website);
    }

    return merge({}, venue, website && { contact: { website } });
  };

  static createVenue = (
    venue: IVenuePostData
  ): Promise<IJSONAPIVenueCollection> =>
    new Promise(async (resolve, reject) => {
      try {
        const cleanVenue = VenueService.getCleanVenue(venue);

        const response: unknown = await APIService.post({
          path: API_ENDPOINTS.VENUES,
          data: cleanVenue,
        });
        resolve(response as IJSONAPIVenueCollection);
      } catch (error) {
        reject(error);
      }
    });

  static getVenue = (
    id: string,
    cancelToken?: CancelToken
  ): Promise<IJSONAPIVenueCollection> =>
    new Promise(async (resolve, reject) => {
      try {
        const path = API_ENDPOINTS.VENUE(id);
        const venue: unknown = await APIService.get({ path, cancelToken });
        resolve(venue as IJSONAPIVenueCollection);
      } catch (error) {
        reject(error);
      }
    });

  static searchVenues = (
    queryParams?:
      | string
      | { [key: string]: string | number | undefined | string[] },
    cancelToken?: any
  ): Promise<IJSONAPIVenueCollection> =>
    new Promise(async (resolve, reject) => {
      try {
        const venueIds: unknown = await APIService.get({
          path: API_ENDPOINTS.SEARCH_VENUES,
          data: queryParams,
          cancelToken,
        });
        resolve(venueIds as IJSONAPIVenueCollection);
      } catch (error) {
        reject(error);
      }
    });

  static getVenues = (
    queryParams?:
      | string
      | { [key: string]: string | number | undefined | string[] }
  ): Promise<IJSONAPIVenueCollection> =>
    new Promise(async (resolve, reject) => {
      try {
        const venueIds: unknown = await APIService.get({
          path: API_ENDPOINTS.VENUES,
          data: queryParams,
        });
        resolve(venueIds as IJSONAPIVenueCollection);
      } catch (error) {
        reject(error);
      }
    });

  static editVenue = (
    venueId: string,
    venue: IVenuePatchData
  ): Promise<IJSONAPIVenueCollection> =>
    new Promise(async (resolve, reject) => {
      try {
        const cleanVenue = VenueService.getCleanVenue(venue);

        const patchedVenue: unknown = await APIService.patch({
          path: API_ENDPOINTS.VENUE(venueId),
          data: cleanVenue,
        });
        resolve(patchedVenue as IJSONAPIVenueCollection);
      } catch (error) {
        reject(error);
      }
    });

  static deleteVenue = (venueId: string): Promise<void> =>
    new Promise(async (resolve, reject) => {
      try {
        await APIService.delete({
          path: API_ENDPOINTS.VENUE(venueId),
        });
        resolve();
      } catch (error) {
        reject(error);
      }
    });

  static editVenueStageOrder = (
    sortedIdList: string[],
    venueId: string
  ): Promise<IJSONAPIVenueCollection> =>
    new Promise(async (resolve, reject) => {
      try {
        const sortedPathList = sortedIdList.map((id) =>
          API_ENDPOINTS.STAGE(id)
        );

        const data = await APIService.patch({
          path: API_ENDPOINTS.VENUE(venueId),
          data: { stages: sortedPathList },
        });

        resolve(data);
      } catch (error) {
        reject(error);
      }
    });

  static editVenueBands = (
    venueId: string,
    bands: string[] | []
  ): Promise<IJSONAPIVenueCollection> =>
    new Promise(async (resolve, reject) => {
      try {
        const data = await APIService.patch({
          path: API_ENDPOINTS.VENUE(venueId),
          data: { bands },
        });

        resolve(data);
      } catch (error) {
        reject(error);
      }
    });

  static publishVenue = (id: string): Promise<void> =>
    new Promise(async (resolve, reject) => {
      try {
        await APIService.post({
          path: API_ENDPOINTS.PUBLISH_VENUE(id),
          isJSON: true,
        });
        resolve();
      } catch (error) {
        reject(error);
      }
    });

  static requestPublishVenue = (id: string): Promise<void> =>
    new Promise(async (resolve, reject) => {
      try {
        await APIService.post({
          path: API_ENDPOINTS.REQUEST_PUBLISH_VENUE(id),
          isJSON: true,
        });
        resolve();
      } catch (error) {
        reject(error);
      }
    });

  static unpublishVenue = (id: string): Promise<void> =>
    new Promise(async (resolve, reject) => {
      try {
        await APIService.post({
          path: API_ENDPOINTS.UNPUBLISH_VENUE(id),
          isJSON: true,
        });
        resolve();
      } catch (error) {
        reject(error);
      }
    });

  static inviteEditor = (
    venueId: string,
    email: string
  ): Promise<IJSONAPIData> =>
    new Promise(async (resolve, reject) => {
      try {
        const venue = API_ENDPOINTS.VENUE(venueId);

        const data = await APIService.post({
          path: API_ENDPOINTS.VENUE_INVITES,
          data: {
            venue,
            email,
          },
        });
        resolve(data);
      } catch (error) {
        reject(error);
      }
    });

  static inviteEditors = (
    venueId: string,
    bandUsers: string[]
  ): Promise<IJSONAPIData> =>
    new Promise(async (resolve, reject) => {
      try {
        const venue = API_ENDPOINTS.VENUE(venueId);

        const data = await APIService.post({
          path: API_ENDPOINTS.VENUE_INVITE_BATCH,
          data: {
            venue,
            bandUsers,
          },
        });
        resolve(data);
      } catch (error) {
        reject(error);
      }
    });

  static revokeEditorInvite = (id: string): Promise<void> =>
    new Promise(async (resolve, reject) => {
      try {
        await APIService.delete({
          path: API_ENDPOINTS.VENUE_INVITE(id),
        });
        resolve();
      } catch (error) {
        reject(error);
      }
    });

  static getEditorInvite = (id: string): Promise<IJSONAPIData> =>
    new Promise(async (resolve, reject) => {
      try {
        const data = await APIService.get({
          path: API_ENDPOINTS.VENUE_INVITE(id),
        });
        resolve(data);
      } catch (error) {
        reject(error);
      }
    });

  static suggestEdit = (data: ISuggestEdit, venueId: string): Promise<void> =>
    new Promise(async (resolve, reject) => {
      try {
        await APIService.post({
          path: API_ENDPOINTS.SUGGEST_EDIT(venueId),
          data,
        });
        resolve();
      } catch (error) {
        reject(error);
      }
    });
}
